import { FC, useState } from "react";
import { scaleLinear } from "d3-scale";
import { interpolateHcl, range } from "d3";

import { VITARI_LANE_COUNT, buildVitariGrid } from "./vitari-grid";

type VitariFlowcellChartProps = {
  data: number[]; // decompressed values, by label index (may be shorter than labels)
  labels: string[];
  minv: number;
  maxv: number;
};

// Schematic pixel sizes, not to scale in millimetres.
const TILE_W = 14;
const TILE_H = 8;
const LANE_GAP = 10;
const HEADER_H = 24;

// The same nine-stop palette and HCL interpolation as FlowcellChart.
const COLORS = [
  "#ffffd9",
  "#edf8b1",
  "#c7e9b4",
  "#7fcdbb",
  "#41b6c4",
  "#1d91c0",
  "#225ea8",
  "#253494",
  "#081d58",
];
const colorStops = range(0, 1, 1.0 / (COLORS.length - 1));
colorStops.push(1);
const colorScale = scaleLinear()
  .domain(colorStops)
  .range(COLORS as never)
  .interpolate(interpolateHcl as never);

const EMPTY_FILL = "#FFFFFF00";

export const VitariFlowcellChart: FC<VitariFlowcellChartProps> = ({
  data,
  labels,
  minv,
  maxv,
}) => {
  const [tooltip, setTooltip] = useState<{
    label: string;
    value: number | null;
    x: number;
    y: number;
  } | null>(null);

  const grid = buildVitariGrid(labels, data);

  return (
    <div>
      {grid.panels.map((panel) => {
        const laneWidth = panel.columns * TILE_W;
        return (
          <div
            key={panel.surface}
            data-testid={`vitari-panel-S${panel.surface}`}
            style={{ marginBottom: 16 }}
          >
            <div
              style={{
                textAlign: "center",
                color: "gray",
                fontWeight: 300,
                marginBottom: 4,
              }}
            >
              S{panel.surface}
            </div>
            <svg
              width={
                VITARI_LANE_COUNT * laneWidth +
                (VITARI_LANE_COUNT - 1) * LANE_GAP
              }
              height={HEADER_H + panel.rows * TILE_H}
            >
              {Array.from({ length: VITARI_LANE_COUNT }, (_, laneIndex) => (
                <text
                  key={laneIndex}
                  data-testid={`vitari-lane-header-L${laneIndex + 1}`}
                  x={laneIndex * (laneWidth + LANE_GAP) + laneWidth / 2}
                  y={HEADER_H - 8}
                  textAnchor="middle"
                  fontSize={11}
                  fill="gray"
                >
                  L{laneIndex + 1}
                </text>
              ))}
              {panel.tiles.map((tile) => (
                <rect
                  key={tile.label}
                  data-testid={`vitari-tile-${tile.label}`}
                  x={
                    (tile.lane - 1) * (laneWidth + LANE_GAP) +
                    (tile.column - 1) * TILE_W
                  }
                  y={HEADER_H + (tile.row - 1) * TILE_H}
                  width={TILE_W}
                  height={TILE_H}
                  rx={1}
                  ry={1}
                  stroke="gray"
                  strokeWidth={1}
                  fill={tileFill(tile.value, minv, maxv)}
                  onMouseEnter={(event) =>
                    setTooltip({
                      label: tile.label,
                      value: tile.value,
                      x: event.clientX,
                      y: event.clientY,
                    })
                  }
                  onMouseMove={(event) =>
                    setTooltip({
                      label: tile.label,
                      value: tile.value,
                      x: event.clientX,
                      y: event.clientY,
                    })
                  }
                  onMouseLeave={() => setTooltip(null)}
                />
              ))}
            </svg>
          </div>
        );
      })}

      {grid.unplaced.length > 0 && (
        <div
          data-testid="vitari-unplaced-note"
          style={{ color: "gray", fontSize: 12 }}
        >
          {grid.unplaced.length} tiles not drawn (label outside lanes 1-
          {VITARI_LANE_COUNT} or not L#R#C#S#)
        </div>
      )}

      {tooltip && (
        <div
          data-testid="vitari-tooltip"
          style={{
            position: "fixed",
            left: tooltip.x + 10,
            top: tooltip.y - 20,
            padding: 4,
            font: "10px sans-serif",
            textAlign: "center",
            background: "white",
            borderRadius: 8,
            boxShadow:
              "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
            pointerEvents: "none",
          }}
        >
          <span style={{ color: "gray" }}>{tooltip.label}</span>
          <br />
          {tooltip.value === null || !Number.isFinite(tooltip.value)
            ? "no value"
            : tooltip.value.toFixed(0)}
        </div>
      )}
    </div>
  );
};

// A tile with no value (null, or NaN from the decompressed array) is drawn empty. A real 0 is coloured.
function tileFill(value: number | null, minv: number, maxv: number): string {
  if (value === null || isNaN(value)) return EMPTY_FILL;

  const scaled = (value - minv) / (maxv - minv + 0.0000001);
  if (scaled > 1) return "purple";
  if (scaled < 0) return "yellow";
  return colorScale(scaled) as unknown as string;
}
